import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { IProfileTag, IProfileTagCreateData } from '@project/view-models';
import { ProfilesTagsApiProviderService } from '@project/data-providers';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { EPageType } from '../../receptionists/services/receptionists-manager.service';

@Injectable({
  providedIn: 'root',
})
export class GroupsManagerService {
  private readonly _groupsPage$ = new BehaviorSubject<IProfileTag[]>([]);
  public readonly groups$ = this._groupsPage$.asObservable();
  public readonly filtersState$ = new BehaviorSubject<{ search: string | null }>({ search: null });
  public readonly hasActionInProgress$ = new BehaviorSubject<boolean>(false);
  private readonly _isFetching$ = new BehaviorSubject<boolean>(false);
  readonly _pagingState$ = new BehaviorSubject<{ currentPageNumber: number; totalPagesCount: number }>({
    currentPageNumber: 1,
    totalPagesCount: 1,
  });
  public readonly isFetching$ = this._isFetching$.asObservable();
  public readonly pagingState$ = this._pagingState$.asObservable();

  public itemsPerPage = 15;
  public currentPage = 1;

  private _pageType$ = new BehaviorSubject<string>(null);

  public readonly _allGroups$ = new BehaviorSubject<IProfileTag[]>([]);
  public lastInviteGroup$ = new BehaviorSubject<IProfileTag>(null);
  public lastDetailedGroup$ = new BehaviorSubject<IProfileTag>(null);

  constructor(private profilesTagsApiProviderService: ProfilesTagsApiProviderService) {}

  public setPageType(type: EPageType): void {
    this._pageType$.next(type);
  }

  private getGroupsFromCacheOrApi(): Observable<IProfileTag[]> {
    const cachedGroups = this._allGroups$.value;
    return cachedGroups.length > 0 ? of(cachedGroups) : this.profilesTagsApiProviderService.getAllGroups();
  }

  private handlePaginationAndCache(groups: IProfileTag[], page: number): void {
    if (this._allGroups$.value.length === 0) {
      this._allGroups$.next(groups);
    }
    this.paginateGroups(groups, page);
  }

  loadGroupsPaginated(page: number): void {
    this._isFetching$.next(true);

    this.getGroupsFromCacheOrApi()
      .pipe(
        tap((groups) => this.handlePaginationAndCache(groups, page)),
        finalize(() => this._isFetching$.next(false)),
      )
      .subscribe();
  }

  public searchGroups(search: string): void {
    this.filtersState$.next({ search });
    this.filterAndPaginateGroups(search);
  }

  private filterAndPaginateGroups(search: string, page: number = 1): void {
    this._isFetching$.next(true);
    const allGroups = this._allGroups$.value;
    const filteredGroups = allGroups.filter((group) => group.name.toLowerCase().includes(search.toLowerCase()));
    this.paginateGroups(filteredGroups, page);
    this._isFetching$.next(false);
  }

  private paginateGroups(groups: IProfileTag[], page: number): void {
    const startIndex = (page - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const paginatedGroups = groups.slice(startIndex, endIndex);

    this._groupsPage$.next(paginatedGroups);
    this._pagingState$.next({
      currentPageNumber: page,
      totalPagesCount: Math.ceil(groups.length / this.itemsPerPage),
    });
  }

  public setDetailGroup(id: string): Observable<IProfileTag> {
    return this.profilesTagsApiProviderService.getGroupById(id).pipe(
      tap((group) => {
        if (group) {
          this.lastDetailedGroup$.next(group);
        }
      }),
    );
  }

  public update(id: string, data: IProfileTagCreateData): Observable<void> {
    return this.profilesTagsApiProviderService.update(id, data);
  }

  public setDetailGroupV2(profileTag: IProfileTag): void {
    this.lastDetailedGroup$.next(profileTag);
  }

  public reloadGroups(): Observable<IProfileTag[]> {
    this._isFetching$.next(true);

    return this.profilesTagsApiProviderService.getAllGroups().pipe(
      tap((groups) => {
        this._allGroups$.next(groups);
        this.paginateGroups(groups, 1);
      }),
      finalize(() => this._isFetching$.next(false)),
      shareReplay(1),
    );
  }

  clearCache(): void {
    this._allGroups$.next([]);
  }
}
